import { useAuthSliceOrTokenRegion } from '@/helpers/getRegion';
import { useAuthSessionMetadata } from '@/lib/auth/hooks/useAuthSessionMetadata';
import { ehrLaunchRegionValueAtom } from '@heidi/integrations/ehr/data/atoms';
import { type Region } from '@heidi/schema/ml-scribe';
import { toValidRegion } from '@heidi/utils';
import { useAtomValue } from 'jotai';

/**
 * The region the user's data resides in — today the Intercom `region-db`
 * attribute. The broker-issued session claim wins, falling back to the
 * EHR-launch and slice/token slots; `undefined` means unknown, so consumers
 * leave the stored value alone rather than stamping a default.
 *
 * Deliberately not `getRegion()`/`useRegion()`: those honour a user-supplied
 * `?region=` and fail open to the AU default, so neither can report "unknown".
 * Both fallbacks are best-effort — the launch slot can carry a launch-supplied
 * region, and the slice half of the third is written by the same launch and
 * query-param writers; only the session claim cannot be seeded that way.
 *
 * Deliberately narrow, too: this is the `region-db` attribute's reader, not a
 * general-purpose region accessor. New callers should keep using
 * `useRegion()`/`getRegion()`; adopt this hook only where an unknown region
 * must stay unknown rather than resolve to a default.
 */
export function useDatabaseRegion(): Region | undefined {
  const sessionMetadata = useAuthSessionMetadata();
  const ehrLaunchRegion = useAtomValue(ehrLaunchRegionValueAtom);
  const sliceOrTokenRegion = useAuthSliceOrTokenRegion();

  return (
    toValidRegion(sessionMetadata?.region) ??
    toValidRegion(ehrLaunchRegion) ??
    sliceOrTokenRegion
  );
}
